import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import AdvanceDataTable from '@/components/core/advance-data-table/advance-data-table';
import { StatStrip } from '@/components/core';
import { Badge } from '@/components/ui-kit/badge';
import { Card, CardContent } from '@/components/ui-kit/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui-kit/select';
import { Skeleton } from '@/components/ui-kit/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui-kit/table';
import {
  formatDateOnly,
  orgCalendarDate,
  toDateOnlyValue,
} from '@/lib/utils/custom-date/custom-date';
import { cn } from '@/lib/utils';
import { createLeaveSheetTableColumns } from '../leave-sheet-table/leave-sheet-table-columns';
import {
  useGetLeaveSheet,
  useGetLeaveSheetTerms,
  useLeaveSheetPolicy,
} from '../../hooks/use-leave-sheet';
import {
  LeaveSheetCategory,
  LeaveSheetResult,
  LeaveSheetRow,
  LeaveSheetTerm,
} from '../../types/leave-sheet.types';

/** "HR Term <period>" for a term picker. Built from the period number, not `HRPeriodName`: that is
 * stored as untranslated "HR Term <n>" and would repeat the prefix. */
export const termLabel = (term: LeaveSheetTerm, t: (key: string) => string) => {
  if (term.HRPeriod) return `${t('HR_TERM')} ${term.HRPeriod}`;
  if (term.HRPeriodName) return term.HRPeriodName;
  return `${t('HR_TERM')} ${formatDateOnly(toDateOnlyValue(term.periodStart))} - ${formatDateOnly(toDateOnlyValue(term.periodEnd))}`;
};

/** A count of days: whole numbers as they are, halves with one decimal. */
const days = (value: number) => (Number.isInteger(value) ? String(value) : value.toFixed(1));

/** The figures of the limited categories (unlimited ones have no allotment to add up). */
export const leaveSheetTotals = (categories: readonly LeaveSheetCategory[]) => {
  const limited = categories.filter((category) => !category.IsUnlimited);
  const sum = (pick: (category: LeaveSheetCategory) => number) =>
    limited.reduce((total, category) => total + pick(category), 0);
  return {
    allocated: sum((category) => category.Allocated),
    taken: sum((category) => category.Taken),
    requested: sum((category) => category.Requested),
    remaining: sum((category) => category.Remaining),
  };
};

type CategoryLine = {
  id: string;
  name: string;
  isDefault?: boolean;
  given: string;
  allocated: string;
  halfDays: string;
  fullDays: string;
  requested: string;
  remaining: string;
  lastUpdated: string;
};

const HEAD = 'h-9 whitespace-nowrap px-2.5 text-[13px] font-medium text-muted-foreground';
const NUM = 'text-right tabular-nums';

/**
 * "Leave by category" (Component Map › Leave Sheet): one row per applicable category, the default leave
 * first, then the TOIL summary row(s) when a `toilBalance` rule binds (TOFF-012): earned TOIL is given,
 * not allotted, and leave taken from it shows as its full days.
 */
const CategoryTable = ({ sheet }: { sheet: LeaveSheetResult }) => {
  const { t } = useTranslation();
  const { summary, policy } = sheet;
  const updated = (iso?: string) => (iso ? formatDateOnly(orgCalendarDate(iso)) : '—');

  const lines: CategoryLine[] = sheet.categories.map((category) => ({
    id: category.LeaveCategoryId,
    name: category.LeaveCategory,
    isDefault: category.IsDefault,
    given: category.IsUnlimited
      ? t('UNLIMITED')
      : category.IsDefault && summary?.leaveCarryForward
        ? t('LEAVE_GIVEN_YEARLY_WITH_CF', { days: days(summary.leaveCarryForward) })
        : t('LEAVE_GIVEN_YEARLY'),
    allocated: category.IsUnlimited ? t('UNLIMITED') : days(category.Allocated),
    halfDays: String(category.HalfDays),
    fullDays: days(category.FullDays),
    requested: days(category.Requested),
    remaining: category.IsUnlimited ? t('UNLIMITED') : days(category.Remaining),
    lastUpdated: updated(category.LastUpdated),
  }));

  if (summary && policy.toil) {
    const toilLine = (id: string, name: string, carried: number, earned: number, taken: number, left: number) => ({
      id,
      name,
      given: t('LEAVE_GIVEN_EARNED'),
      allocated: days(carried + earned),
      halfDays: '—',
      fullDays: days(taken),
      requested: '—',
      remaining: days(left),
      lastUpdated: '—',
    });
    if (policy.toil === 'split') {
      lines.push(
        toilLine(
          'weekend-toil',
          t('WEEKEND_TOIL'),
          summary.weekendToilCarryForward,
          summary.weekendToilEarned,
          summary.weekendToilTaken,
          summary.weekendToilLeft
        ),
        toilLine(
          'special-toil',
          t('SPECIAL_TOIL'),
          summary.specialToilCarryForward,
          summary.specialToilEarned,
          summary.specialToilTaken,
          summary.specialToilLeft
        )
      );
    } else {
      lines.push(
        toilLine(
          'toil',
          t('TOIL'),
          summary.toilCarryForward,
          summary.toilEarned,
          summary.toilCarryForward + summary.toilEarned - summary.toilLeft,
          summary.toilLeft
        )
      );
    }
  }
  if (!lines.length) return null;

  const columns: [keyof CategoryLine, string, boolean][] = [
    ['given', t('LEAVE_HOW_GIVEN'), false],
    ['allocated', t('ALLOCATED_LEAVE'), true],
    ['halfDays', t('HALF_DAYS'), true],
    ['fullDays', t('FULL_DAYS'), true],
    ['requested', t('REQUESTED_LEAVES'), true],
    ['remaining', t('REMAINING_LEAVE'), true],
    ['lastUpdated', t('LAST_UPDATED'), false],
  ];

  return (
    <>
      {/* On a phone each category is a card: its figures two per line. */}
      <div className="flex flex-col gap-3 md:hidden">
        {lines.map((line) => (
          <div key={line.id} className="flex flex-col gap-3 rounded-lg border bg-card p-4">
            <div className="flex items-center gap-2 font-semibold">
              <span className="break-words">{line.name}</span>
              {line.isDefault && <Badge variant="outline">{t('DEFAULT')}</Badge>}
            </div>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2">
              {columns.map(([key, label]) => (
                <div key={key} className="flex min-w-0 flex-col gap-0.5">
                  <dt className="text-xs text-muted-foreground">{label}</dt>
                  <dd className="break-words text-sm font-medium tabular-nums">{line[key]}</dd>
                </div>
              ))}
            </dl>
          </div>
        ))}
      </div>
      <Table divClassname="max-md:hidden">
        <TableHeader className="[&_tr]:border-b [&_tr]:border-input">
          <TableRow className="hover:bg-transparent">
            <TableHead className={HEAD}>{t('CATEGORY')}</TableHead>
            {columns.map(([key, label, numeric]) => (
              <TableHead key={key} className={cn(HEAD, numeric && 'text-right')}>
                {label}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {lines.map((line) => (
            <TableRow key={line.id} className="hover:bg-transparent">
              <TableCell className="h-10 px-2.5 py-1 font-medium">
                <span className="flex items-center gap-2">
                  {line.name}
                  {line.isDefault && <Badge variant="outline">{t('DEFAULT')}</Badge>}
                </span>
              </TableCell>
              {columns.map(([key, , numeric]) => (
                <TableCell
                  key={key}
                  className={cn('whitespace-nowrap px-2.5 py-1', numeric ? NUM : 'text-muted-foreground')}
                >
                  {line[key]}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </>
  );
};

/** The ledger's rows by month, newest month last as the sheet runs; the carry-forward row opens the first. */
const monthsOf = (rows: readonly LeaveSheetRow[]) => {
  const months = new Map<string, LeaveSheetRow[]>();
  let current = '';
  rows.forEach((row) => {
    if (row.date) current = row.date.slice(0, 7);
    const key = current || 'start';
    months.set(key, [...(months.get(key) ?? []), row]);
  });
  return [...months.entries()];
};

/** The day-by-day ledger of the default leave, one small table per month. */
const Ledger = ({ sheet }: { sheet: LeaveSheetResult }) => {
  const { t, i18n } = useTranslation();
  const columns = createLeaveSheetTableColumns({
    t,
    policy: sheet.policy,
    defaultCategory: sheet.summary?.defaultCategory ?? '',
  });
  const months = monthsOf(sheet.rows);
  if (!months.length) {
    return <p className="py-4 text-sm text-muted-foreground">{t('NO_DATA_FOUND')}</p>;
  }
  const monthLabel = (key: string) =>
    key === 'start'
      ? t('LEAVE_SHEET_CARRIED_FORWARD')
      : new Date(`${key}-01T00:00:00Z`).toLocaleDateString(i18n.language, {
          timeZone: 'UTC',
          month: 'long',
          year: 'numeric',
        });

  return (
    <div className="flex flex-col gap-6">
      {months.map(([key, rows]) => {
        const taken = rows.reduce((sum, row) => sum + row.leaveDays, 0);
        const earned = rows.reduce((sum, row) => sum + (row.kind === 'toil' ? row.toilDays : 0), 0);
        return (
          <section key={key} className="flex flex-col gap-2">
            <h3 className="flex flex-wrap items-baseline gap-x-3 text-sm font-semibold">
              {monthLabel(key)}
              <span className="text-xs font-normal text-muted-foreground">
                {t('LEAVE_SHEET_MONTH_TOTALS', { taken: days(taken), earned: days(earned) })}
              </span>
            </h3>
            <AdvanceDataTable
              data={rows}
              columns={columns}
              isLoading={false}
              error={null}
              pagination={{ pageIndex: 0, pageSize: rows.length, totalCount: rows.length }}
              isExpandRowContent={false}
              mobileView="cards"
              hidePager
            />
          </section>
        );
      })}
    </div>
  );
};

/**
 * One employee's Leave Sheet (HR Module Prototype, Component Map › Time Off › Leave Sheet; replaces the
 * Leave Overview and its Leave Details drawer): the HR term picker, an Allocated / Taken / Requested /
 * Remaining strip, "Leave by category" with the TOIL summary, then the day-by-day ledger of the default
 * leave grouped by month. Used on Team Time Off (for the picked employee) and My Time Off (for the
 * signed-in user); `key` it by user id so the term resets when the employee changes. `canPickTerm`
 * offers the employee's earlier HR terms; `showLedger` is off for those who may see the figures but
 * not the day-by-day sheet.
 */
export const LeaveSheetView = ({
  userId,
  canPickTerm = false,
  showLedger = true,
}: {
  userId: string;
  canPickTerm?: boolean;
  showLedger?: boolean;
}) => {
  const { t } = useTranslation();
  const [termId, setTermId] = useState<string | undefined>(undefined);

  const { policy, error: policyError } = useLeaveSheetPolicy(userId);
  const { data: terms, isLoading: isTermsLoading } = useGetLeaveSheetTerms(userId, canPickTerm);
  const { data: sheet, isLoading, error } = useGetLeaveSheet(userId, termId, policy);

  if (policyError) {
    return <p className="text-sm text-destructive">{t('LEAVE_SHEET_POLICY_LOAD_FAILED')}</p>;
  }
  if (!policy || isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-9 w-64" />
        <Skeleton className="h-20 rounded-xl" />
        <Skeleton className="h-40 rounded-xl" />
        {showLedger && <Skeleton className="h-64 rounded-xl" />}
      </div>
    );
  }
  if (error)
    return (
      <p className="text-sm text-destructive">
        {error instanceof Error ? error.message : String(error)}
      </p>
    );
  if (!sheet) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-sm text-muted-foreground">
          {t('NO_CURRENT_HR_TERM')}
        </CardContent>
      </Card>
    );
  }

  const totals = leaveSheetTotals(sheet.categories);
  const period = `${formatDateOnly(toDateOnlyValue(sheet.term.periodStart))} – ${formatDateOnly(toDateOnlyValue(sheet.term.periodEnd))}`;

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        {canPickTerm && isTermsLoading && <Skeleton className="h-9 w-56" />}
        {canPickTerm && terms && terms.length > 0 ? (
          <Select
            value={termId ?? terms.find((term) => term.isCurrent)?.ItemId ?? terms[0].ItemId}
            onValueChange={setTermId}
          >
            <SelectTrigger className="h-9 w-full sm:w-56" aria-label={t('SELECT_HR_TERM')}>
              <SelectValue placeholder={t('SELECT_HR_TERM')} />
            </SelectTrigger>
            <SelectContent>
              {terms.map((term) => (
                <SelectItem key={term.ItemId} value={term.ItemId}>
                  {termLabel(term, t)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <span className="text-sm font-semibold">{termLabel(sheet.term, t)}</span>
        )}
        <span className="text-sm tabular-nums text-muted-foreground">{period}</span>
      </div>

      <StatStrip
        items={[
          { label: t('ALLOCATED_LEAVE'), value: days(totals.allocated) },
          { label: t('TAKEN_LEAVES'), value: days(totals.taken) },
          { label: t('REQUESTED_LEAVES'), value: days(totals.requested) },
          { label: t('REMAINING_LEAVE'), value: days(totals.remaining) },
        ]}
      />

      <section className="flex flex-col gap-2">
        <h2 className="text-base font-semibold">{t('LEAVE_BY_CATEGORY')}</h2>
        <CategoryTable sheet={sheet} />
      </section>

      {showLedger && (
        <section className="flex flex-col gap-2">
          <h2 className="text-base font-semibold">{t('LEAVE_SHEET')}</h2>
          {sheet.summary ? (
            <Ledger sheet={sheet} />
          ) : (
            <p className="text-sm text-muted-foreground">{t('LEAVE_SHEET_NO_DEFAULT_CATEGORY')}</p>
          )}
        </section>
      )}
    </div>
  );
};
